<template>
    <div class="official-dashboard-page">
        <LoadingOverlay :visible="isPageLoading" />
        <!-- Onion-style full-width Tabs wrapper (role-based, via API) -->
        <div class="onion-tabs-wrapper mb-2 mb-sm-3">
            <!-- Layer 1: Group (Total / Business* or Total / Shop*) -->
            <ul
                class="nav nav-tabs onion-layer onion-layer-top flex-wrap mb-1"
                v-if="effectiveRole !== 'basic'"
            >
                <template v-if="!hasTopTabOverflow">
                    <li class="nav-item" v-for="tab in topTabs" :key="tab">
                        <a
                            href="#"
                            class="nav-link"
                            :class="{ active: activeTopTab === tab }"
                            @click.prevent="
                                activeTopTab = tab;
                                activeModelTab = ALL_MODELS_KEY;
                                reloadDashboard();
                            "
                        >
                            {{ getTopTabLabel(tab) }}
                        </a>
                    </li>
                </template>
                <template v-else>
                    <li class="nav-item" :key="primaryTopTab">
                        <a
                            href="#"
                            class="nav-link"
                            :class="{ active: activeTopTab === primaryTopTab }"
                            @click.prevent="
                                activeTopTab = primaryTopTab;
                                activeModelTab = ALL_MODELS_KEY;
                                reloadDashboard();
                            "
                        >
                            {{ getTopTabLabel(primaryTopTab) }}
                        </a>
                    </li>
                    <li class="nav-item ml-2" key="overflow-select">
                        <select
                            class="form-select form-select-sm more-tabs-select"
                            v-model="overflowSelection"
                            @change="handleOverflowChange"
                        >
                            <option value="" disabled>...</option>
                            <option
                                v-for="tab in overflowTabs"
                                :key="tab"
                                :value="tab"
                            >
                                {{ getTopTabLabel(tab) }}
                            </option>
                        </select>
                    </li>
                </template>
            </ul>

            <!-- Layer 2: Device Model (Total / BTW350 / BT2400P) -->
            <ul class="nav nav-pills onion-layer flex-wrap">
                <li class="nav-item" v-for="tab in modelTabs" :key="tab">
                    <a
                        href="#"
                        class="nav-link"
                        :class="{ active: activeModelTab === tab }"
                        @click.prevent="
                            activeModelTab = tab;
                            reloadDashboard();
                        "
                    >
                        {{ getModelTabLabel(tab) }}
                    </a>
                </li>
            </ul>
        </div>

        <div class="content">
            <div
                class="row mb-2"
                v-if="effectiveRole !== 'basic' && activeTopTab"
            >
                <div
                    class="col-12 d-flex justify-content-between align-items-center"
                >
                    <div class="text-muted small mb-1">
                        {{ $t('chart.chart_msg__text_showing_statistics_for') }}
                        <strong>{{ getTopTabLabel(activeTopTab) }}</strong>
                        /
                        <strong>{{ getModelTabLabel(activeModelTab) }}</strong>
                    </div>
                    <div
                        class="text-muted small text-end info-tooltip"
                        :title="$t('chart.chart_msg__text_last_updated_info')"
                    >
                        <i class="fa fa-info-circle"></i>
                        {{ $t('chart.chart_msg__text_last_updated') }}
                        <strong>{{ lastUpdatedTime }}</strong>
                    </div>
                </div>
                <div class="col-12">
                    <hr class="section-divider" />
                </div>
            </div>
            <div
                v-for="section in visibleSections"
                :key="section.id"
                :class="[
                    'row',
                    'mb-3',
                    section.type === 'charts-row' ? 'chart-equal-row' : '',
                ]"
            >
                <template v-if="section.type === 'cards-row'">
                    <div
                        v-for="item in section.items"
                        :key="item.id"
                        class="p-2 col-xl-3 col-lg-4 col-md-6 col-sm-12"
                        v-show="metrics[item.metricKey]"
                    >
                        <StatsCard v-if="metrics[item.metricKey]">
                            <template #header>
                                <div
                                    class="icon-big text-center"
                                    :class="`icon-${
                                        metrics[item.metricKey].type
                                    }`"
                                >
                                    <i
                                        :class="metrics[item.metricKey].icon"
                                    ></i>
                                </div>
                            </template>
                            <template #content>
                                <div class="numbers">
                                    <p class="card-category">
                                        {{
                                            $t(
                                                'chart.' +
                                                    metrics[item.metricKey]
                                                        .title
                                            )
                                        }}
                                    </p>
                                    <h4 class="card-title">
                                        {{ metrics[item.metricKey].value }}
                                    </h4>
                                </div>
                            </template>
                            <template #footer>
                                <hr />
                                <div
                                    class="stats d-flex align-items-center justify-content-between"
                                >
                                    <div
                                        class="trend d-flex align-items-center"
                                    >
                                        <i
                                            :class="[
                                                'fa',
                                                metrics[item.metricKey].trend
                                                    .direction === 'up'
                                                    ? 'fa-arrow-up text-success'
                                                    : metrics[item.metricKey]
                                                          .trend.direction ===
                                                      'down'
                                                    ? 'fa-arrow-down text-danger'
                                                    : 'fa-minus text-muted',
                                            ]"
                                        ></i>
                                        <span class="ml-2 trend-value">
                                            {{
                                                metrics[item.metricKey].trend
                                                    .value
                                            }}
                                        </span>
                                    </div>
                                    <div
                                        class="info-tooltip"
                                        :title="
                                            $t(
                                                'chart.' +
                                                    metrics[item.metricKey].info
                                            )
                                        "
                                    >
                                        <i class="fa fa-info-circle"></i>
                                    </div>
                                </div>
                            </template>
                        </StatsCard>
                    </div>
                </template>

                <!-- charts row: fully driven by group_chart blocks and items -->
                <template v-else-if="section.type === 'charts-row'">
                    <template
                        v-for="item in section.items || []"
                        :key="item && item.id"
                    >
                        <div
                            v-if="getChartForItem(item)"
                            :class="[
                                `col-xl-${item.width || 6}`,
                                'col-lg-12',
                                'col-md-12',
                                'col-sm-12',
                                'mb-4',
                                'p-2',
                                'chart-equal-col',
                            ]"
                        >
                            <div
                                v-if="
                                    isCombinedVariantItem(item) &&
                                    (getVariantsForItem(item).length > 1 ||
                                        isTestResultCombinedItem(item))
                                "
                                class="d-flex flex-wrap justify-content-end align-items-center mb-2"
                            >
                                <div class="d-flex flex-wrap gap-2">
                                    <template
                                        v-if="isTestResultCombinedItem(item)"
                                    >
                                        <select
                                            class="form-select form-select-sm"
                                            style="width: 160px"
                                            v-model="modeSelection[item.id]"
                                        >
                                            <option
                                                v-for="mode in charts[
                                                    item.chartKey
                                                ]?.modeOptions || []"
                                                :key="mode.code"
                                                :value="mode.code"
                                            >
                                                {{ $t(mode.label) }}
                                            </option>
                                        </select>
                                        <select
                                            v-if="
                                                charts[item.chartKey]
                                                    ?.rangeOptionsByMode &&
                                                (charts[item.chartKey]
                                                    ?.rangeOptionsByMode[
                                                    modeSelection[item.id] ||
                                                        charts[item.chartKey]
                                                            ?.selectedMode
                                                ]?.length || 0) > 0
                                            "
                                            class="form-select form-select-sm"
                                            style="width: 160px"
                                            v-model="rangeSelection[item.id]"
                                        >
                                            <option
                                                v-for="rng in charts[
                                                    item.chartKey
                                                ].rangeOptionsByMode[
                                                    modeSelection[item.id] ||
                                                        charts[item.chartKey]
                                                            ?.selectedMode
                                                ]"
                                                :key="rng"
                                                :value="rng"
                                            >
                                                {{ rng }}
                                            </option>
                                        </select>
                                    </template>
                                    <template v-else>
                                        <select
                                            class="form-select form-select-sm"
                                            style="width: 180px"
                                            v-model="
                                                topFiveVariantSelection[item.id]
                                            "
                                        >
                                            <option
                                                v-for="variant in getVariantsForItem(
                                                    item
                                                )"
                                                :key="variant.code"
                                                :value="variant.code"
                                            >
                                                {{ $t(variant.label) }}
                                            </option>
                                        </select>
                                    </template>
                                </div>
                            </div>
                            <ChartCard
                                class="chart-card-equal"
                                :title="
                                    $t('chart.' + getChartForItem(item).title)
                                "
                                :subTitle="
                                    isSubTitleVisible(getChartForItem(item))
                                        ? $t(
                                              getChartForItem(item).subTitle ||
                                                  ''
                                          )
                                        : ''
                                "
                                :chartData="getChartForItem(item).data"
                                :chartOptions="getChartForItem(item).options"
                                :chartColors="chartColors"
                                :chartType="getChartForItem(item).type"
                                :chartPercentages="
                                    getChartForItem(item).percentages
                                "
                                :rawDataShowPercent="
                                    Boolean(
                                        getChartForItem(item).percentages
                                    ) || getChartForItem(item).type === 'pie'
                                "
                                :infoText="
                                    $t('chart.' + getChartForItem(item).info)
                                "
                                :loading="isChartLoading(item)"
                            />
                        </div>
                    </template>
                </template>

                <div class="col-12">
                    <hr class="section-divider" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { ChartCard, StatsCard } from '@/components';
import { fetchAdvancedDashboardConfigFromAPI } from '@/api/advanced-dashboard';
import { fetchClubs, fetchStations } from '@/api/users';
import { randomInt, buildTestResultTrendChart } from './demoChartsConfig';
import LoadingOverlay from '@/components/Loading/LoadingOverlay.vue';
import { isLoading, setLoading } from '@/plugins/loading';
import { formatDateTimeInZone, resolveTimeZone } from '@/utils/timezone';

export default {
    name: 'AdvancedDashboard',
    components: {
        ChartCard,
        StatsCard,
        LoadingOverlay,
    },
    setup() {
        const store = useStore();
        const { t } = useI18n();
        const userSelf = computed(() => store.state.auth.userSelf || {});
        const preferredTimeZone = computed(() => {
            return resolveTimeZone(
                userSelf.value?.time_zone ||
                    userSelf.value?.timeZone ||
                    userSelf.value?.timezone ||
                    null
            );
        });
        const userClubId = computed(
            () =>
                userSelf.value?.club ||
                userSelf.value?.club_id ||
                userSelf.value?.clubId ||
                null
        );
        const userStations = computed(() =>
            Array.isArray(userSelf.value?.stations)
                ? userSelf.value.stations
                : []
        );
        const getStationId = station => {
            if (station === null || station === undefined) return null;
            if (typeof station === 'string' || typeof station === 'number') {
                return station;
            }
            if (
                station.station &&
                typeof station.station === 'object' &&
                Object.keys(station.station).length
            ) {
                const nested = getStationId(station.station);
                if (nested) return nested;
            }
            return (
                station.id ||
                station.station_id ||
                station.stationId ||
                station.stationID ||
                station.stationid ||
                station.station ||
                station.station_code ||
                null
            );
        };
        const chartColors = [
            '#7a9e9f',
            '#41b883',
            '#f3bb45',
            '#68b3c8',
            '#eb5e28',
        ];
        const SUBTITLE_VISIBLE_KEYS = ['battery_test_test_type_quarterly'];
        const sections = ref([]);
        const metrics = ref({});
        const charts = ref({});
        const chartLoadingMap = ref({});
        const chartFetchStatus = ref({});
        const activeRequestId = ref(0);
        const pendingChartFetches = ref(0);
        const lastUpdatedRaw = ref(null);
        const lastUpdatedTime = computed(() => {
            const formatted = formatDateTimeInZone(
                lastUpdatedRaw.value,
                preferredTimeZone.value,
                {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                }
            );
            return formatted || '--';
        });

        const COMBINED_VARIANT_KEYS = [
            'top_five_active_shop_combined',
            'top_five_active_user_combined',
            'fw_version_combined',
            'app_version_combined',
            'battery_test_result_past_monthly_combined',
        ];
        const TEST_RESULT_COMBINED_KEY =
            'battery_test_result_past_monthly_combined';
        const variantSelection = ref({});
        const modeSelection = ref({});
        const rangeSelection = ref({});
        const topFiveVariantSelection = variantSelection; // alias for template binding

        // --- Role & tabs (via API: /users/clubs, /users/stations) ---
        const apiRole = ref(null); // 'superadmin' | 'business' | 'shop' | null
        const clubs = ref([]);
        const stations = ref([]);

        const loadStationsForCurrentUser = async () => {
            try {
                const stationsResponse = await fetchStations();
                if (
                    stationsResponse &&
                    stationsResponse.error === 'forbidden'
                ) {
                    return { forbidden: true };
                }
                if (Array.isArray(stationsResponse)) {
                    stations.value = stationsResponse;
                    return { loaded: true };
                }
            } catch (nestedError) {
                console.error(nestedError);
            }
            return { loaded: false };
        };

        const detectRoleFromApi = async () => {
            try {
                const clubsResponse = await fetchClubs();

                if (Array.isArray(clubsResponse)) {
                    clubs.value = clubsResponse;

                    if (clubsResponse.length > 1) {
                        apiRole.value = 'superadmin';
                        return;
                    }
                    if (clubsResponse.length === 1) {
                        apiRole.value = 'business';
                        await loadStationsForCurrentUser();
                        return;
                    }
                    // 長度為 0 則維持 null，交由後續邏輯決定
                }

                const { loaded, forbidden } =
                    await loadStationsForCurrentUser();
                if (forbidden) {
                    // 無權限存取 stations，視為 basic
                    apiRole.value = 'basic';
                    return;
                }
                if (loaded) {
                    apiRole.value = 'shop';
                }
            } catch (error) {
                console.error(error);
            }
        };

        const effectiveRole = computed(() => {
            if (apiRole.value) {
                return apiRole.value;
            }
            // 預設視為 shop，以保守權限顯示
            return 'shop';
        });
        const analyticsRole = computed(() =>
            effectiveRole.value === 'basic' ? 'shop' : effectiveRole.value
        );

        // Internal constant for 'All models' tab - used as key
        const ALL_MODELS_KEY = 'all_models';
        // Internal constant for 'Overall' tab - used as key
        const OVERALL_KEY = 'overall';

        const modelTabs = ref([ALL_MODELS_KEY]);
        const modelTabDeviceMap = ref({ [ALL_MODELS_KEY]: 'Total' });
        const chartFetchPlanRef = ref([]);

        const activeTopTab = ref('');
        const activeModelTab = ref(ALL_MODELS_KEY);

        // Translate model tab for display
        const getModelTabLabel = key => {
            if (key === ALL_MODELS_KEY) {
                return t('chart.chart_tab__all_models');
            }
            return key;
        };

        // Translate top tab for display
        const getTopTabLabel = key => {
            if (key === OVERALL_KEY) {
                return t('chart.chart_tab__overall');
            }
            return key;
        };

        const isTotalModelSelected = computed(() => {
            const map = modelTabDeviceMap.value || {};
            return (map[activeModelTab.value] || 'Total') === 'Total';
        });

        const deviceFromTab = computed(() => {
            const map = modelTabDeviceMap.value || {};
            return map[activeModelTab.value] || 'Total';
        });

        const topTabs = computed(() => {
            if (effectiveRole.value === 'superadmin') {
                const clubNames = clubs.value
                    .map(item => item.name || item.id || item.club_name)
                    .filter(Boolean);
                return [OVERALL_KEY, ...clubNames];
            }
            if (effectiveRole.value === 'business') {
                const firstClub = clubs.value[0] || {};
                const clubName =
                    firstClub.name || firstClub.id || firstClub.club_name;
                const stationNames = stations.value
                    .map(
                        item =>
                            item.name ||
                            item.id ||
                            item.station_name ||
                            item.station_id
                    )
                    .filter(Boolean);
                if (clubName) {
                    return [clubName, ...stationNames];
                }
                return stationNames.length
                    ? stationNames
                    : [t('chart.chart_tab__this_business')];
            }
            if (effectiveRole.value === 'shop') {
                const stationNames = stations.value
                    .map(
                        item =>
                            item.name ||
                            item.id ||
                            item.station_name ||
                            item.station_id
                    )
                    .filter(Boolean);
                if (stationNames.length) {
                    return stationNames;
                }
                const firstStation = stations.value[0] || {};
                const stationName =
                    firstStation.name ||
                    firstStation.id ||
                    firstStation.station_name ||
                    firstStation.station_id;
                return stationName
                    ? [stationName]
                    : [t('chart.chart_tab__this_shop')];
            }
            if (effectiveRole.value === 'basic') {
                return [];
            }
            return [];
        });

        const hasTopTabOverflow = computed(
            () => (topTabs.value || []).length > 6
        );
        const primaryTopTab = computed(() =>
            topTabs.value && topTabs.value.length ? topTabs.value[0] : ''
        );
        const overflowTabs = computed(() =>
            hasTopTabOverflow.value ? topTabs.value.slice(1) : []
        );
        const overflowSelection = ref('');

        watch(
            topTabs,
            newTabs => {
                if (!newTabs || newTabs.length === 0) {
                    activeTopTab.value = '';
                    return;
                }

                if (!newTabs.includes(activeTopTab.value)) {
                    activeTopTab.value = newTabs[0];
                }
            },
            { immediate: true }
        );
        watch(
            [activeTopTab, overflowTabs],
            ([currentTab, currentOverflow]) => {
                if (currentOverflow.includes(currentTab)) {
                    overflowSelection.value = currentTab;
                } else {
                    overflowSelection.value = '';
                }
            },
            { immediate: true }
        );

        const findClubByTab = tab => {
            return (
                clubs.value.find(
                    c =>
                        c.name === tab ||
                        c.club_name === tab ||
                        String(c.id) === String(tab) ||
                        String(c.club_id) === String(tab)
                ) ||
                clubs.value[0] ||
                null
            );
        };

        const findStationByTab = (tab, useFallback = true) => {
            const allStations = [
                ...(Array.isArray(stations.value) ? stations.value : []),
                ...(Array.isArray(userStations.value)
                    ? userStations.value
                    : []),
            ];
            const matched = allStations.find(
                s =>
                    s.name === tab ||
                    s.station_name === tab ||
                    String(s.id) === String(tab) ||
                    String(s.station) === String(tab) ||
                    String(s.station_id) === String(tab)
            );
            return matched || (useFallback ? allStations[0] || null : null);
        };

        const selectedLevelParams = computed(() => {
            const role = effectiveRole.value;
            if (role === 'superadmin') {
                if (activeTopTab.value === OVERALL_KEY) {
                    return { level: 'global', levelId: null };
                }
                const club = findClubByTab(activeTopTab.value);
                return {
                    level: 'club',
                    levelId: club ? club.id || club.club_id || null : null,
                };
            }
            if (role === 'business') {
                const station = findStationByTab(activeTopTab.value, false);
                if (station) {
                    return {
                        level: 'station',
                        levelId: getStationId(station),
                    };
                }
                const club =
                    clubs.value[0] || findClubByTab(activeTopTab.value);
                return {
                    level: 'club',
                    levelId: club ? club.id || club.club_id || null : null,
                };
            }
            if (role === 'shop') {
                const station = findStationByTab(activeTopTab.value);
                return {
                    level: 'station',
                    levelId: station ? getStationId(station) : null,
                };
            }
            // basic or fallback
            const station =
                (Array.isArray(userStations.value) &&
                    userStations.value.length > 0 &&
                    userStations.value[0]) ||
                findStationByTab(activeTopTab.value, false) ||
                null;
            return {
                level: 'station',
                levelId: getStationId(station),
            };
        });

        const syncModelTabsFromDevices = devices => {
            const tabs = [];
            const map = {};
            const seen = new Set();

            const pushDevice = device => {
                const dev = device || 'Total';
                if (seen.has(dev)) return;
                seen.add(dev);
                const label = dev === 'Total' ? ALL_MODELS_KEY : dev;
                tabs.push(label);
                map[label] = dev;
            };

            pushDevice('Total');
            (Array.isArray(devices) ? devices : []).forEach(pushDevice);

            modelTabs.value = tabs;
            modelTabDeviceMap.value = map;

            if (!map[activeModelTab.value]) {
                activeModelTab.value = tabs[0] || ALL_MODELS_KEY;
            }
        };

        const markChartLoading = (code, value) => {
            if (!code) return;
            chartLoadingMap.value = {
                ...chartLoadingMap.value,
                [code]: value,
            };
        };

        const makeChartFetchKey = (code, device) =>
            `${code || ''}__${device || 'Total'}`;

        const areAllChartsResolved = () =>
            Object.values(chartFetchStatus.value || {}).every(
                value => value === true
            );

        const isActiveRequest = requestId =>
            requestId && requestId === activeRequestId.value;

        const beginDashboardLoad = () => {
            const requestId = activeRequestId.value + 1;
            activeRequestId.value = requestId;
            chartLoadingMap.value = {};
            chartFetchStatus.value = {};
            pendingChartFetches.value = 0;
            setLoading(true);
            return requestId;
        };

        const stopPageLoadingIfSettled = requestId => {
            if (!isActiveRequest(requestId)) return;
            if (pendingChartFetches.value === 0 && areAllChartsResolved()) {
                setLoading(false);
            }
        };

        const stopPageLoadingAfterSkeleton = requestId => {
            if (!isActiveRequest(requestId)) return;
            setLoading(false);
        };

        const setChartFetchPlan = (plan, requestId) => {
            if (!isActiveRequest(requestId)) return;
            const status = {};
            (Array.isArray(plan) ? plan : []).forEach(item => {
                const key = makeChartFetchKey(item.code, item.device);
                status[key] = false;
            });
            chartFetchStatus.value = status;
            pendingChartFetches.value = Object.keys(status).length;
            if (pendingChartFetches.value === 0) {
                stopPageLoadingIfSettled(requestId);
            }
        };

        const handleChartFetchStart = (code, device, requestId) => {
            if (!isActiveRequest(requestId)) return;
            const key = makeChartFetchKey(code, device);
            if (!(key in chartFetchStatus.value)) {
                chartFetchStatus.value = {
                    ...chartFetchStatus.value,
                    [key]: false,
                };
                pendingChartFetches.value += 1;
            }
            markChartLoading(code, true);
        };

        const handleChartFetchEnd = (code, device, requestId) => {
            if (!isActiveRequest(requestId)) return;
            const key = makeChartFetchKey(code, device);
            if (key in chartFetchStatus.value) {
                chartFetchStatus.value = {
                    ...chartFetchStatus.value,
                    [key]: true,
                };
            }
            pendingChartFetches.value = Math.max(
                0,
                pendingChartFetches.value - 1
            );
            markChartLoading(code, false);
            stopPageLoadingIfSettled(requestId);
        };

        const buildPlaceholderCharts = sectionsList => {
            const map = {};
            (sectionsList || []).forEach(section => {
                if (section.type !== 'charts-row') return;
                (section.items || []).forEach(item => {
                    if (!item || !item.chartKey) return;
                    if (map[item.chartKey]) return;
                    map[item.chartKey] = {
                        id: item.chartKey,
                        title: item.chartKey,
                        subTitle: '',
                        type: 'bar',
                        data: { labels: [], datasets: [] },
                        options: {},
                        placeholder: true,
                    };
                });
            });
            return map;
        };

        const buildPlaceholderMetrics = sectionsList => {
            const map = {};
            (sectionsList || []).forEach(section => {
                if (section.type !== 'cards-row') return;
                (section.items || []).forEach(item => {
                    if (!item || !item.metricKey) return;
                    if (map[item.metricKey]) return;
                    map[item.metricKey] = {
                        title: item.metricKey,
                        icon: 'fa fa-info-circle',
                        type: 'primary',
                        info: '',
                        value: '--',
                        trend: {
                            direction: 'flat',
                            value: '--',
                        },
                    };
                });
            });
            return map;
        };

        const loadDashboard = async () => {
            const params = selectedLevelParams.value;
            const requestId = beginDashboardLoad();
            try {
                const {
                    sections: layoutSections,
                    metrics: layoutMetrics,
                    charts: layoutCharts,
                    latestCalculateDate,
                    availableDevices,
                } = await fetchAdvancedDashboardConfigFromAPI(
                    {
                        role: analyticsRole.value,
                        topTab: activeTopTab.value,
                        model: activeModelTab.value,
                        device: deviceFromTab.value,
                        level: params.level,
                        levelId: params.levelId,
                        timeZone: preferredTimeZone.value,
                    },
                    {
                        onLayoutReady: payload => {
                            if (!isActiveRequest(requestId)) return;
                            syncModelTabsFromDevices(payload?.availableDevices);
                            sections.value = payload?.sections || [];
                            chartFetchPlanRef.value =
                                payload?.chartFetchPlan || [];
                            charts.value = buildPlaceholderCharts(
                                payload?.sections || []
                            );
                            metrics.value = buildPlaceholderMetrics(
                                payload?.sections || []
                            );
                            const initialLoading = {};
                            (payload?.chartFetchPlan || []).forEach(item => {
                                if (item && item.code) {
                                    initialLoading[item.code] = true;
                                }
                            });
                            chartLoadingMap.value = initialLoading;
                            stopPageLoadingAfterSkeleton(requestId);
                        },
                        onChartFetchPlan: plan =>
                            setChartFetchPlan(plan, requestId),
                        onChartFetchStart: (code, device) =>
                            handleChartFetchStart(code, device, requestId),
                        onChartFetchEnd: (code, device) =>
                            handleChartFetchEnd(code, device, requestId),
                        onChartData: progress => {
                            if (!isActiveRequest(requestId)) return;
                            if (progress?.metrics) {
                                metrics.value = progress.metrics;
                            }
                            if (progress?.charts) {
                                charts.value = progress.charts;
                            }
                            if (progress?.latestCalculateDate) {
                                lastUpdatedRaw.value =
                                    progress.latestCalculateDate;
                            }
                        },
                    }
                );

                if (!isActiveRequest(requestId)) {
                    return;
                }

                syncModelTabsFromDevices(availableDevices);
                sections.value = layoutSections;
                metrics.value = layoutMetrics;
                charts.value = layoutCharts;
                lastUpdatedRaw.value = latestCalculateDate || null;
            } finally {
                if (isActiveRequest(requestId)) {
                    pendingChartFetches.value = 0;
                    stopPageLoadingIfSettled(requestId);
                }
            }
        };

        // --- Combined top-five chart handling (shops/users) ---
        const isCombinedVariantItem = item =>
            item && COMBINED_VARIANT_KEYS.includes(item.chartKey);
        const isTestResultCombinedItem = item =>
            item && item.chartKey === TEST_RESULT_COMBINED_KEY;

        const getVariantsForItem = item => {
            if (!item || !item.chartKey) return [];
            const chart = charts.value[item.chartKey];
            if (!chart) return [];
            return chart.variants || [];
        };

        const getChartForItem = item => {
            if (!item || !item.chartKey) return null;
            const chart = charts.value[item.chartKey];
            if (!chart) return null;

            if (chart.placeholder) {
                return chart;
            }

            if (!isCombinedVariantItem(item)) {
                return chart;
            }

            if (isTestResultCombinedItem(item)) {
                const modeSelected =
                    modeSelection.value[item.id] ||
                    chart.selectedMode ||
                    'monthly';
                const ranges = (chart.rangeOptionsByMode || {})[modeSelected];
                const rangeSelected =
                    rangeSelection.value[item.id] ||
                    (Array.isArray(ranges) && ranges.length ? ranges[0] : null);
                const key = `${modeSelected}-${
                    rangeSelected === null ? 'all' : rangeSelected
                }`;
                const found = chart.chartsByKey ? chart.chartsByKey[key] : null;
                return (
                    found || Object.values(chart.chartsByKey || {})[0] || null
                );
            }

            const variants = chart.variants || [];
            if (!variants.length) return null;

            const selectedCode = variantSelection.value[item.id];
            const selectedVariant =
                variants.find(v => v.code === selectedCode) || variants[0];

            return selectedVariant.chart || chart;
        };

        const isSubTitleVisible = chart =>
            Boolean(
                chart &&
                    chart.subTitle &&
                    (chart.showSubTitle ||
                        SUBTITLE_VISIBLE_KEYS.includes(chart.id))
            );

        const isChartLoading = item => {
            if (!item || !item.chartKey) return false;
            if (!isCombinedVariantItem(item)) {
                return Boolean(chartLoadingMap.value[item.chartKey]);
            }
            if (isTestResultCombinedItem(item)) {
                const chart = charts.value[item.chartKey];
                const baseCode = chart && chart.baseCode;
                return (
                    Boolean(chartLoadingMap.value[item.chartKey]) ||
                    Boolean(chartLoadingMap.value[baseCode])
                );
            }
            const variants = item.variants || [];
            return variants.some(variant =>
                Boolean(chartLoadingMap.value[variant.code])
            );
        };

        const syncVariantSelection = () => {
            const next = { ...variantSelection.value };

            (sections.value || []).forEach(section => {
                if (section.type !== 'charts-row') return;

                (section.items || []).forEach(item => {
                    if (!isCombinedVariantItem(item)) return;
                    if (isTestResultCombinedItem(item)) return;

                    const variants = getVariantsForItem(item);
                    if (!variants.length) return;

                    const existing = next[item.id];
                    const exists = variants.some(v => v.code === existing);
                    next[item.id] = exists ? existing : variants[0].code;
                });
            });

            variantSelection.value = next;
        };

        const syncTestResultSelection = () => {
            const nextMode = { ...modeSelection.value };
            const nextRange = { ...rangeSelection.value };

            (sections.value || []).forEach(section => {
                if (section.type !== 'charts-row') return;
                (section.items || []).forEach(item => {
                    if (!isTestResultCombinedItem(item)) return;
                    const chart = charts.value[item.chartKey];
                    if (!chart) return;
                    const modes = chart.modeOptions || [];
                    if (!modes.length) return;

                    const mode =
                        modes.find(m => m.code === nextMode[item.id]) ||
                        modes[0];
                    nextMode[item.id] = mode.code;

                    const ranges = chart.rangeOptionsByMode
                        ? chart.rangeOptionsByMode[mode.code] || []
                        : [];
                    if (ranges.length) {
                        const candidate = ranges.find(
                            r => r === nextRange[item.id]
                        );
                        nextRange[item.id] =
                            candidate !== undefined ? candidate : ranges[0];
                    } else {
                        delete nextRange[item.id];
                    }
                });
            });

            modeSelection.value = nextMode;
            rangeSelection.value = nextRange;
        };

        const syncRangeOnModeChange = () => {
            const nextRange = { ...rangeSelection.value };

            (sections.value || []).forEach(section => {
                if (section.type !== 'charts-row') return;
                (section.items || []).forEach(item => {
                    if (!isTestResultCombinedItem(item)) return;
                    const chart = charts.value[item.chartKey];
                    if (!chart) return;
                    const mode =
                        modeSelection.value[item.id] || chart.selectedMode;
                    const ranges = chart.rangeOptionsByMode
                        ? chart.rangeOptionsByMode[mode] || []
                        : [];
                    if (ranges.length) {
                        const existing = nextRange[item.id];
                        nextRange[item.id] = ranges.includes(existing)
                            ? existing
                            : ranges[0];
                    } else {
                        delete nextRange[item.id];
                    }
                });
            });

            rangeSelection.value = nextRange;
        };

        watch([sections, charts], syncVariantSelection, {
            immediate: true,
        });
        watch([sections, charts], syncTestResultSelection, {
            immediate: true,
        });
        watch(modeSelection, syncRangeOnModeChange, { deep: true });

        // --- Charts helpers (groupId no longer used; keep stubs for compatibility) ---

        const getChartsByGroup = () => [];

        const visibleSections = computed(() => {
            const role = effectiveRole.value;
            const chartMap = charts.value || {};
            const metricsMap = metrics.value || {};

            return sections.value.filter(section => {
                // 1) 先依角色過濾
                if (
                    section.allowedRoles &&
                    section.allowedRoles.length > 0 &&
                    !section.allowedRoles.includes(role)
                ) {
                    return false;
                }

                // 2) cards-row 檢查是否至少有一個 metric 在目前 model 下可見
                if (section.type === 'cards-row') {
                    const items = section.items || [];
                    const hasVisibleMetric = items.some(
                        item => item && metricsMap[item.metricKey]
                    );
                    return hasVisibleMetric;
                }

                // 3) charts-row 檢查是否至少有一張 chart 在目前 model 下可見
                if (section.type === 'charts-row') {
                    const items = section.items || [];
                    const hasVisibleChart = items.some(
                        item => item && chartMap[item.chartKey]
                    );
                    return hasVisibleChart;
                }

                // 其他型別，通過角色檢查即可顯示
                return true;
            });
        });

        const reloadDashboard = () => {
            loadDashboard();
        };

        const handleOverflowChange = () => {
            if (!overflowSelection.value) {
                return;
            }
            activeTopTab.value = overflowSelection.value;
            activeModelTab.value = ALL_MODELS_KEY;
            reloadDashboard();
        };

        watch(
            userClubId,
            (next, prev) => {
                if (effectiveRole.value === 'basic' && next && next !== prev) {
                    reloadDashboard();
                }
            },
            { immediate: false }
        );
        watch(
            userStations,
            (next, prev) => {
                if (
                    effectiveRole.value === 'basic' &&
                    Array.isArray(next) &&
                    next.length &&
                    next !== prev
                ) {
                    reloadDashboard();
                }
            },
            { immediate: false, deep: true }
        );
        watch(
            preferredTimeZone,
            (next, prev) => {
                if (!next || next === prev) return;
                reloadDashboard();
            },
            { immediate: false }
        );

        onMounted(async () => {
            await detectRoleFromApi();
            await loadDashboard();
        });

        // --- Version charts (firmware/app versions + upload volume) ---
        const versionPeriod = ref('total');

        const versionPeriodLabel = computed(() => {
            switch (versionPeriod.value) {
                case 'day':
                    return t('chart.chart_selector__period_day');
                case 'week':
                    return t('chart.chart_selector__period_week');
                case 'month':
                    return t('chart.chart_selector__period_month');
                case 'season':
                    return t('chart.chart_selector__period_season');
                case 'year':
                    return t('chart.chart_selector__period_year');
                case 'total':
                    return t('chart.chart_selector__period_total');
                default:
                    return '';
            }
        });

        const versionCharts = computed(() => {
            const baseCharts = getChartsByGroup('versions');

            const getRangeForPeriod = period => {
                switch (period) {
                    case 'day':
                        return [20, 80];
                    case 'week':
                        return [80, 220];
                    case 'month':
                        return [150, 360];
                    case 'season':
                        return [260, 520];
                    case 'year':
                        return [500, 1200];
                    case 'total':
                    default:
                        return [800, 1800];
                }
            };

            const [min, max] = getRangeForPeriod(versionPeriod.value);

            return baseCharts.map(chart => {
                const labels = (chart.data && chart.data.labels) || [];
                const sourceDatasets =
                    (chart.data && chart.data.datasets) || [];

                const datasets = sourceDatasets.map((ds, index) => {
                    if (index !== 0) {
                        return ds;
                    }

                    return {
                        ...ds,
                        data: labels.map(() => randomInt(min, max)),
                    };
                });

                return {
                    ...chart,
                    data: {
                        ...(chart.data || {}),
                        datasets,
                    },
                };
            });
        });

        const uploadVolumeCharts = computed(() =>
            getChartsByGroup('uploadVolume')
        );

        // --- Test result over time (dynamic monthly / quarterly with range) ---
        const testResultMode = ref('monthly'); // 'monthly' | 'quarterly'
        const testResultRange = ref(6); // number of months or quarters

        const monthlyRangeOptions = computed(() =>
            Array.from({ length: 24 - 6 + 1 }, (_, index) => {
                const value = 6 + index;
                return {
                    value,
                    label: t('chart.chart_selector__range_months', {
                        count: value,
                    }),
                };
            })
        );

        const quarterlyRangeOptions = computed(() =>
            Array.from({ length: 8 - 4 + 1 }, (_, index) => {
                const value = 4 + index;
                return {
                    value,
                    label: t('chart.chart_selector__range_quarters', {
                        count: value,
                    }),
                };
            })
        );

        const currentTestResultRangeOptions = computed(() =>
            testResultMode.value === 'monthly'
                ? monthlyRangeOptions.value
                : quarterlyRangeOptions.value
        );

        const findChartById = id => charts.value[id] || null;

        const testResultTrendBaseChart = computed(() => {
            if (testResultMode.value === 'monthly') {
                return findChartById('testResultMonthly');
            }
            return findChartById('testResultQuarterly');
        });

        const testResultTrendChart = computed(() => {
            const base = testResultTrendBaseChart.value;
            if (!base || !base.data) {
                return null;
            }

            const labels = Array.isArray(base.data.labels)
                ? base.data.labels
                : [];

            if (!labels.length) {
                return null;
            }

            const sliceSize = Math.min(
                typeof testResultRange.value === 'number'
                    ? testResultRange.value
                    : 0,
                labels.length
            );

            return buildTestResultTrendChart(base, sliceSize);
        });

        const testResultRangeLabel = computed(() => {
            const unit =
                testResultMode.value === 'monthly' ? 'months' : 'quarters';
            return `Last ${testResultRange.value} ${unit}`;
        });

        const testResultTrendSubTitle = computed(() => {
            const base = testResultTrendBaseChart.value;
            const baseSub = (base && base.subTitle) || 'Result counts';
            const modeLabel =
                testResultMode.value === 'monthly'
                    ? 'per month'
                    : 'per quarter';
            return `${baseSub} (${testResultRangeLabel.value}, ${modeLabel})`;
        });

        // --- Active shops & users (with period selectors) ---
        const activeShopsPeriod = ref('day');
        const activeUsersPeriod = ref('day');

        const activeShopsChart = computed(() => {
            switch (activeShopsPeriod.value) {
                case 'day':
                    return findChartById('top5ShopsYesterday');
                case 'week':
                    return findChartById('top5Shops7Days');
                case 'month':
                    return findChartById('top5ShopsMonth');
                case 'quarter':
                    return findChartById('top5ShopsQuarter');
                case 'year':
                    return findChartById('top5ShopsYear');
                default:
                    return null;
            }
        });

        const activeShopsSubTitle = computed(() => {
            switch (activeShopsPeriod.value) {
                case 'day':
                    return t('chart.chart_selector__top5_shops_yesterday');
                case 'week':
                    return t('chart.chart_selector__top5_shops_last_7_days');
                case 'month':
                    return t('chart.chart_selector__top5_shops_this_month');
                case 'quarter':
                    return t('chart.chart_selector__top5_shops_this_quarter');
                case 'year':
                    return t('chart.chart_selector__top5_shops_this_year');
                default:
                    return '';
            }
        });

        const activeShopsPeriodLabel = computed(() => {
            switch (activeShopsPeriod.value) {
                case 'day':
                    return t('chart.chart_selector__period_yesterday');
                case 'week':
                    return t('chart.chart_selector__period_last_7_days');
                case 'month':
                    return t('chart.chart_selector__period_this_month');
                case 'quarter':
                    return t('chart.chart_selector__period_this_quarter');
                case 'year':
                    return t('chart.chart_selector__period_this_year');
                default:
                    return '';
            }
        });

        const activeUsersChart = computed(() => {
            switch (activeUsersPeriod.value) {
                case 'day':
                    return findChartById('top5UsersYesterday');
                case 'week':
                    return findChartById('top5Users7Days');
                case 'month':
                    return findChartById('top5UsersMonth');
                case 'quarter':
                    return findChartById('top5UsersQuarter');
                case 'year':
                    return findChartById('top5UsersYear');
                default:
                    return null;
            }
        });

        const activeUsersSubTitle = computed(() => {
            switch (activeUsersPeriod.value) {
                case 'day':
                    return t('chart.chart_selector__top5_users_yesterday');
                case 'week':
                    return t('chart.chart_selector__top5_users_last_7_days');
                case 'month':
                    return t('chart.chart_selector__top5_users_this_month');
                case 'quarter':
                    return t('chart.chart_selector__top5_users_this_quarter');
                case 'year':
                    return t('chart.chart_selector__top5_users_this_year');
                default:
                    return '';
            }
        });

        const activeUsersPeriodLabel = computed(() => {
            switch (activeUsersPeriod.value) {
                case 'day':
                    return t('chart.chart_selector__period_yesterday');
                case 'week':
                    return t('chart.chart_selector__period_last_7_days');
                case 'month':
                    return t('chart.chart_selector__period_this_month');
                case 'quarter':
                    return t('chart.chart_selector__period_this_quarter');
                case 'year':
                    return t('chart.chart_selector__period_this_year');
                default:
                    return '';
            }
        });

        const emptyChartData = {
            labels: [],
            datasets: [],
        };

        // --- Other grouped charts ---
        const testResultRatioCharts = computed(() =>
            getChartsByGroup('testResultRatio')
        );

        const usersGrowthCharts = computed(() =>
            getChartsByGroup('usersGrowth')
        );

        const testTypeBatteryCharts = computed(() =>
            getChartsByGroup('testTypeBattery')
        );

        return {
            sections,
            metrics,
            charts,
            chartLoadingMap,
            chartColors,
            topFiveVariantSelection,
            getChartForItem,
            getVariantsForItem,
            isCombinedVariantItem,
            isChartLoading,
            isSubTitleVisible,
            lastUpdatedTime,
            effectiveRole,
            clubs,
            stations,
            topTabs,
            hasTopTabOverflow,
            primaryTopTab,
            overflowTabs,
            overflowSelection,
            modelTabs,
            activeTopTab,
            activeModelTab,
            isTotalModelSelected,
            reloadDashboard,
            handleOverflowChange,
            visibleSections,
            isPageLoading: isLoading,
            // i18n helpers and constants
            ALL_MODELS_KEY,
            OVERALL_KEY,
            getModelTabLabel,
            getTopTabLabel,
            // version & upload
            versionPeriod,
            versionPeriodLabel,
            versionCharts,
            uploadVolumeCharts,
            // test result trend
            testResultMode,
            testResultRange,
            currentTestResultRangeOptions,
            testResultTrendChart,
            testResultTrendSubTitle,
            testResultRangeLabel,
            // active shops & users
            activeShopsPeriod,
            activeShopsChart,
            activeShopsSubTitle,
            activeShopsPeriodLabel,
            activeUsersPeriod,
            activeUsersChart,
            activeUsersSubTitle,
            activeUsersPeriodLabel,
            emptyChartData,
            // grouped charts
            testResultRatioCharts,
            usersGrowthCharts,
            testTypeBatteryCharts,
            // test result combined selectors
            modeSelection,
            rangeSelection,
            isTestResultCombinedItem,
        };
    },
};
</script>

<style scoped>
.chart-equal-row {
    align-items: stretch;
}

.chart-equal-col {
    display: flex;
    flex-direction: column;
}

:deep(.chart-card-equal) {
    flex: 1;
}
</style>
